import { DateTime } from 'luxon';
import type { StatusColor, StatusRange, StatusValue } from '@/shared/schema';

/**
 * Determines the current status based on the status value configuration.
 * For manual mode, returns the manually selected status.
 * For scheduled mode, checks if any range is currently active and returns the appropriate status.
 *
 * @param statusValue - The status field value containing mode, ranges, and default status
 * @param referenceDate - Optional date to use for comparison (defaults to current date)
 * @returns The current active status color
 */
export function getCurrentStatus(
  statusValue: StatusValue | undefined | null,
  referenceDate?: Date
): StatusColor {
  if (!statusValue) {
    return 'red'; // Default fallback (inactive)
  }

  // Manual mode - return the manually selected status or default
  if (statusValue.mode === 'manual') {
    return statusValue.manualStatus || statusValue.defaultStatus || 'red';
  }

  // Scheduled mode - check if any range is currently active
  const now = referenceDate ? DateTime.fromJSDate(referenceDate, { zone: 'utc' }) : DateTime.utc();
  const today = now.startOf('day');

  if (!statusValue.ranges || statusValue.ranges.length === 0) {
    return statusValue.defaultStatus || 'red';
  }

  // Sort ranges by start date to handle them in order
  const sortedRanges = [...statusValue.ranges].sort((a, b) => {
    const dateA = DateTime.fromISO(a.startDate);
    const dateB = DateTime.fromISO(b.startDate);
    return dateA.toMillis() - dateB.toMillis();
  });

  // Track the status after the most recent ended range
  let lastEndedRangeFallback: StatusColor | undefined;

  for (const range of sortedRanges) {
    const startDate = DateTime.fromISO(range.startDate).startOf('day');
    const endDate = DateTime.fromISO(range.endDate).startOf('day');

    // Check if the current date is within this range (inclusive)
    if (today >= startDate && today <= endDate) {
      return range.statusDuring;
    }

    // Track the fallback from the most recent ended range
    if (today > endDate) {
      lastEndedRangeFallback = range.fallbackAfter;
    }
  }

  // If we had a range that ended with a fallback, use that
  if (lastEndedRangeFallback) {
    return lastEndedRangeFallback;
  }

  // Default status when no range is active
  return statusValue.defaultStatus || 'red';
}

/**
 * Finds the next upcoming range from the status value.
 *
 * @param statusValue - The status field value
 * @param referenceDate - Optional date to use for comparison (defaults to current date)
 * @returns The next upcoming range, or undefined if none
 */
export function getNextUpcomingRange(
  statusValue: StatusValue | undefined | null,
  referenceDate?: Date
): StatusRange | undefined {
  if (!statusValue || statusValue.mode !== 'scheduled' || !statusValue.ranges) {
    return undefined;
  }

  const now = referenceDate ? DateTime.fromJSDate(referenceDate, { zone: 'utc' }) : DateTime.utc();
  const today = now.startOf('day');

  // Find ranges that start in the future
  const futureRanges = statusValue.ranges
    .filter((range) => {
      const startDate = DateTime.fromISO(range.startDate).startOf('day');
      return startDate > today;
    })
    .sort((a, b) => {
      const dateA = DateTime.fromISO(a.startDate);
      const dateB = DateTime.fromISO(b.startDate);
      return dateA.toMillis() - dateB.toMillis();
    });

  return futureRanges[0];
}

/**
 * Finds the currently active range from the status value.
 *
 * @param statusValue - The status field value
 * @param referenceDate - Optional date to use for comparison (defaults to current date)
 * @returns The currently active range, or undefined if none
 */
export function getCurrentActiveRange(
  statusValue: StatusValue | undefined | null,
  referenceDate?: Date
): StatusRange | undefined {
  if (!statusValue || statusValue.mode !== 'scheduled' || !statusValue.ranges) {
    return undefined;
  }

  const now = referenceDate ? DateTime.fromJSDate(referenceDate, { zone: 'utc' }) : DateTime.utc();
  const today = now.startOf('day');

  return statusValue.ranges.find((range) => {
    const startDate = DateTime.fromISO(range.startDate).startOf('day');
    const endDate = DateTime.fromISO(range.endDate).startOf('day');
    return today >= startDate && today <= endDate;
  });
}
